/* eslint-disable max-lines */
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import * as React from 'react';
import { FC, useEffect } from 'react';
import {
  BillingLeverDetails,
  EnumOption,
  FilterOption,
  PricingCatalogByLeverId,
} from '../../services/data/PricingCatalogInterfaces';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import { InventoryGroup } from '../../CommonInterfaces';
import { PricingCatalogService } from '../../services/PricingCatalogService';
import { Price } from '../../services/data/PricingV2Interfaces';
import ErrorMessage from '../ErrorMessage';
import DeleteIcon from '@mui/icons-material/Delete';

interface AddNewPriceModalProps {
  priceToUpdate?: Price;
  pricingCatalog: PricingCatalogByLeverId;
  filterOptions: FilterOption[];
  inventoryGroups: InventoryGroup[];
  onSubmitButtonClick(newPrice: Price): void;
  onCloseClicked(): void;
}

// eslint-disable-next-line complexity -- I'm not sure how to break this out any more since its fairly self conatined
const AddNewPriceModal: FC<AddNewPriceModalProps> = (props) => {
  const filterOptionsByLeverId = React.useMemo(
    () => PricingCatalogService.groupFiltersByLeverId(props.filterOptions),
    [props.filterOptions],
  );
  const inUpdateMode = props.priceToUpdate != null;
  const [category, setCategory] = React.useState<string>(
    props.pricingCatalog[props.priceToUpdate?.pricingLeverId]?.product_name,
  );
  const [lever, setLever] = React.useState<BillingLeverDetails>(
    props.pricingCatalog[props.priceToUpdate?.pricingLeverId],
  );

  const filterOptionsMapByFilterId = React.useMemo(() => {
    const filtersForLever = filterOptionsByLeverId[lever?.id] || [];
    const map: { [filterId: number]: FilterOption } = {};
    for (const filter of filtersForLever) {
      map[filter.pricing_lever_filter_id] = filter;
    }
    return map;
  }, [lever, filterOptionsByLeverId]);

  const initialSelectedFilters = () => {
    return (
      props.priceToUpdate?.priceFilterValues.map((item) => filterOptionsMapByFilterId[item.pricingLeverFilterId]) || []
    );
  };

  const initialFilterOptions = () => {
    const filterOptionsForLever = filterOptionsByLeverId[props.priceToUpdate?.pricingLeverId] || [];
    return filterOptionsForLever.filter(
      (item) => !(item.input_type === 'inventory_group_name' && props.inventoryGroups.length === 0),
    );
  };

  const initialSelectedFilterValues = (): EnumOption[] => {
    return (
      props.priceToUpdate?.priceFilterValues.map((item) => {
        const filterOption = filterOptionsMapByFilterId[item.pricingLeverFilterId];
        if (filterOption?.input_type === 'enum_type') {
          const enumOption = filterOption.enum_options.find((opt) => opt.value === item.value);
          return enumOption || { value: item.value, display_name: item.value };
        }
        return { value: item.value, display_name: item.value };
      }) || []
    );
  };

  const [leverOptions, setLeverOptions] = React.useState<BillingLeverDetails[]>([]);
  const [leverSuffix, setLeverSuffix] = React.useState<string>(props.priceToUpdate?.rateDescriptionSuffix || null);
  const [unitOfMeasure, setUnitOfMeasure] = React.useState<string>(props.priceToUpdate?.unitOfMeasure);
  const [uomOptions, setUomOptions] = React.useState<string[]>([]);
  const [warehousePrice, setWarehousePrice] = React.useState<number>(Number(props.priceToUpdate?.warehouseFee) || null);
  const [shipperPrice, setShipperPrice] = React.useState<number>(Number(props.priceToUpdate?.shipperFee) || null);
  const [error, setError] = React.useState<string>('');
  const [filterOptions, setFilterOptions] = React.useState<FilterOption[]>(initialFilterOptions);
  const [selectedFilterOptions, setSelectedFilterOptions] =
    React.useState<(FilterOption | null)[]>(initialSelectedFilters);
  const [selectedFilterValues, setSelectedFilterValues] = React.useState<EnumOption[]>(initialSelectedFilterValues);
  const [editSuffix, setEditSuffix] = React.useState<Boolean>(false);
  const isInitialSuffixLoad = React.useRef(true);

  const fullRateDescription = React.useMemo(() => {
    const leverDisplayName = lever?.display_name || '';
    if (leverSuffix) {
      return leverDisplayName + ' - ' + leverSuffix;
    }
    return leverDisplayName;
  }, [lever, leverSuffix]);

  useEffect(() => {
    const filteredLeverOptions =
      Object.values(props.pricingCatalog).filter((item) => item.product_name === category && item.patConfiguredLever) ||
      [];
    const sortedOptions = filteredLeverOptions.sort((a, b) => a.display_name.localeCompare(b.display_name));
    setLeverOptions(sortedOptions);
    if (!inUpdateMode) {
      setLever(null);
      setUnitOfMeasure('');
    }
  }, [category]);

  useEffect(() => {
    setUomOptions(props.pricingCatalog[lever?.id]?.units.sort() || []);
    if (!inUpdateMode) {
      setUnitOfMeasure('');
      resetFiltersForLever();
    }
  }, [lever]);

  useEffect(() => {
    if (editSuffix) return; // Do not auto-update suffix while in edit mode
    const generatedSuffix = generateSuffix();
    if (isInitialSuffixLoad.current) {
      // Dont auto-gen the suffix on the initial load, it will overwrite any custom suffix
      // in edit mode
      isInitialSuffixLoad.current = false;
      return;
    }

    setLeverSuffix(generatedSuffix);
  }, [selectedFilterOptions, selectedFilterValues, editSuffix]);

  const generateSuffix = (): string => {
    let tempLeverSuffix = null;
    // There is an assumption that the index on the options and the values will always be 1-1
    selectedFilterOptions.forEach((filterOption: FilterOption, index) => {
      const selectedValue = selectedFilterValues[index];
      if (filterOption && selectedValue?.value) {
        let suffixToAdd = '';
        if (filterOption.input_type === 'boolean' && selectedValue.value === 'false') {
          suffixToAdd = 'Not ' + filterOption.display_name;
        } else if (filterOption.input_type === 'boolean' && selectedValue.value === 'true') {
          suffixToAdd = filterOption.display_name;
        } else if (filterOption.input_type === 'integer') {
          suffixToAdd = filterOption.display_name + ' ' + selectedValue.value;
        } else {
          suffixToAdd = selectedValue.display_name;
        }
        tempLeverSuffix = tempLeverSuffix?.concat(' - ', suffixToAdd) || suffixToAdd;
      }
    });
    return tempLeverSuffix;
  };

  const inventoryGroupDescriptions = () => {
    return props.inventoryGroups.map((group) => group.description) || [];
  };

  const resetFiltersForLever = () => {
    const filters = filterOptionsByLeverId[lever?.id] || [];
    const requiredFilters = filters.filter((item) => item.required);
    setFilterOptions(
      filters.filter(
        (item) => !(item.input_type === 'inventory_group_name' && inventoryGroupDescriptions().length === 0),
      ),
    );
    setSelectedFilterOptions(requiredFilters);
    setSelectedFilterValues(new Array(requiredFilters.length).fill(''));
  };

  const baseTextFieldProps = {
    variant: 'outlined' as const,
    className: 'new-price-modal-entry',
    InputLabelProps: {
      shrink: false,
    },
  };

  const submitButtonClicked = async () => {
    setError('');

    try {
      validatePrice();
      props.onSubmitButtonClick({
        pricingLeverId: lever?.id,
        rateDescriptionSuffix: leverSuffix,
        unitOfMeasure,
        warehouseFee: warehousePrice.toString(),
        shipperFee: shipperPrice.toString(),
        reservationScopeId: 0,
        priceFilterValues: [
          ...selectedFilterOptions
            .filter((selectedFilterOption) => selectedFilterOption !== null)
            .map((selectedFilterOption, index) => {
              return {
                pricingLeverFilterId: selectedFilterOption.pricing_lever_filter_id,
                value: selectedFilterValues[index].value,
              };
            }),
        ],
      });
    } catch (e) {
      setError(e.message);
    }
  };

  const validatePrice = () => {
    if (!lever) {
      throw new Error('Please select a lever');
    }
    if (!unitOfMeasure) {
      throw new Error('Please select a UOM');
    }
    if (!shipperPrice) {
      throw new Error('Please enter a shipper price');
    }
    if (!warehousePrice) {
      throw new Error('Please enter a warehouse price');
    }
    selectedFilterOptions.forEach((selectedFilterOption, index) => {
      if (selectedFilterOption?.required && !selectedFilterValues[index]) {
        throw new Error('Please enter a value for all required modifiers');
      }
    });
    return;
  };

  const closeClicked = async () => {
    props.onCloseClicked();
  };

  const getDistinctCategories = (): string[] => {
    return Array.from(new Set(Object.values(props.pricingCatalog).map((item) => item.product_name)));
  };

  const addNewFilterOption = () => {
    setSelectedFilterOptions([...selectedFilterOptions, null]);
  };

  const setSelectedFilterOption = (index: number, filterId: number) => {
    const filterOption = filterOptionsMapByFilterId[filterId];
    if (index >= selectedFilterOptions.length) {
      setSelectedFilterOptions([...selectedFilterOptions, filterOption]);
      setSelectedFilterValues([...selectedFilterValues, { value: '', display_name: '' }]);
    } else {
      const tempSelectedFilterOptions = [...selectedFilterOptions];
      tempSelectedFilterOptions[index] = filterOption;
      setSelectedFilterOptions(tempSelectedFilterOptions);
      const tempSelectedFilterValues = [...selectedFilterValues];
      tempSelectedFilterValues[index] = { value: '', display_name: '' };
      setSelectedFilterValues(tempSelectedFilterValues);
    }
    setFilterOptions(filterOptions);
  };

  const setSelectedFilterValue = (index: number, value: EnumOption) => {
    if (index >= selectedFilterOptions.length) {
      setSelectedFilterValues([...selectedFilterValues, value]);
    } else {
      const tempSelectedFilterValues = [...selectedFilterValues];
      tempSelectedFilterValues[index] = value;
      setSelectedFilterValues(tempSelectedFilterValues);
    }
  };

  const deleteSelectedFilterOption = (index: number) => {
    const tempSelectedFilterOptions = [...selectedFilterOptions];
    const tempSelectedFilterValues = [...selectedFilterValues];
    tempSelectedFilterOptions.splice(index, 1);
    tempSelectedFilterValues.splice(index, 1);
    setSelectedFilterOptions(tempSelectedFilterOptions);
    setSelectedFilterValues(tempSelectedFilterValues);
  };

  const renderSelectedFilterOptions = () => {
    return (
      <div>
        {selectedFilterOptions.map((selectedFilterOption, index) => {
          const inputType = getFilterOptionEntryType(selectedFilterOption);
          const filterTextFieldProps = {
            'id': 'new-price-modal-filter-value',
            'data-testid': 'new-price-modal-filter-value',
            ...baseTextFieldProps,
          };
          return (
            <Grid container columnSpacing={1}>
              <Grid item xs={1}>
                {!selectedFilterOption?.required && (
                  <IconButton aria-label="delete" onClick={() => deleteSelectedFilterOption(index)}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </Grid>
              <Grid item xs={5}>
                <TextField
                  select
                  id="new-price-modal-filter"
                  label="Select Modifier"
                  value={selectedFilterOption?.pricing_lever_filter_id || ''}
                  data-testid="new-price-modal-filter"
                  disabled={selectedFilterOption?.required}
                  {...baseTextFieldProps}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setSelectedFilterOption(index, event.target.value as any);
                  }}
                >
                  <MenuItem key={''} value={''}>
                    {''}
                  </MenuItem>
                  {filterOptions.map((filterOption) => (
                    <MenuItem
                      key={filterOption.pricing_lever_filter_id}
                      value={filterOption.pricing_lever_filter_id}
                      disabled={selectedFilterOptions
                        .map((item) => item?.pricing_lever_filter_id)
                        .includes(filterOption.pricing_lever_filter_id)}
                    >
                      {filterOption.display_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                {inputType === 'select' && (
                  <TextField
                    select
                    label="Select Option"
                    {...filterTextFieldProps}
                    value={selectedFilterValues[index] || ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setSelectedFilterValue(index, event.target.value as unknown as EnumOption);
                    }}
                    SelectProps={{
                      renderValue: (selected: any) => selected?.display_name || '',
                    }}
                  >
                    {getFilterSelectEntries(selectedFilterOption)}
                  </TextField>
                )}
                {inputType !== 'select' && (
                  <TextField
                    type={inputType}
                    label="Enter Value"
                    {...filterTextFieldProps}
                    value={selectedFilterValues[index]?.value || ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = event.target;
                      setSelectedFilterValue(index, { value, display_name: value });
                    }}
                  />
                )}
              </Grid>
            </Grid>
          );
        })}
      </div>
    );
  };

  const getFilterOptionEntryType = (filterOption: FilterOption): string => {
    switch (filterOption?.input_type) {
      case 'integer':
        return 'number';
      case 'enum_type':
      case 'boolean':
      case 'inventory_group_name':
        return 'select';
      default:
        return 'text';
    }
  };

  const getFilterSelectEntries = (filterOption: FilterOption): JSX.Element[] => {
    switch (filterOption?.input_type) {
      case 'enum_type':
        const enumOptions = [{ display_name: '', value: '' }, ...filterOption.enum_options];
        if (filterOption.is_array === true) {
          const locationCategoryCombos = [
            { display_name: 'Bin, Rack and Floor', value: '["bin_storage", "floor_storage", "rack_storage"]' },
            { display_name: 'Bin and Rack', value: '["bin_storage", "rack_storage"]' },
            { display_name: 'Bin and Floor', value: '["bin_storage", "floor_storage"]' },
            { display_name: 'Rack and Floor', value: '["rack_storage", "floor_storage"]' },
            { display_name: 'Bin', value: '["bin_storage"]' },
            { display_name: 'Floor', value: '["floor_storage"]' },
            { display_name: 'Rack', value: '["rack_storage"]' },
          ];
          return locationCategoryCombos.map((combo) => (
            <MenuItem key={combo.value} value={combo as any}>
              {combo.display_name}
            </MenuItem>
          ));
        } else {
          return enumOptions.map((enumOption) => (
            <MenuItem key={enumOption.value} value={enumOption as any}>
              {enumOption.display_name}
            </MenuItem>
          ));
        }
      case 'boolean':
        const boolOptions: EnumOption[] = [
          { value: '', display_name: '' },
          { value: 'true', display_name: 'true' },
          { value: 'false', display_name: 'false' },
        ];
        return boolOptions.map((boolOption) => (
          <MenuItem key={boolOption.value} value={boolOption as any}>
            {boolOption.display_name}
          </MenuItem>
        ));
      case 'inventory_group_name':
        const invGroups: EnumOption[] = [
          { value: '', display_name: '' },
          ...inventoryGroupDescriptions().map((d) => ({ value: d, display_name: d })),
        ];
        return invGroups.map((invGroup) => (
          <MenuItem key={invGroup.value} value={invGroup as any}>
            {invGroup.display_name}
          </MenuItem>
        ));
      default:
        return null;
    }
  };

  return (
    <Dialog open={true} onClose={closeClicked} className="new-price-modal">
      <DialogTitle>{inUpdateMode ? 'Update Pricing Lever' : 'New Pricing Lever'}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={closeClicked}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        {error && <ErrorMessage data-testid="new-price-modal-error-message">{error}</ErrorMessage>}
        <h3>Base</h3>
        <Grid container columnSpacing={1}>
          <Grid item xs={6}>
            <InputLabel className="new-price-modal-label">Category:</InputLabel>
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              id="new-price-modal-category"
              label="Select Category"
              value={category}
              data-testid="new-price-modal-category"
              {...baseTextFieldProps}
              disabled={inUpdateMode}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setCategory(event.target.value as any);
              }}
            >
              <MenuItem key={''} value={''}>
                {''}
              </MenuItem>
              {getDistinctCategories().map((categoryOption) => (
                <MenuItem key={categoryOption} value={categoryOption}>
                  {categoryOption}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <InputLabel className="new-price-modal-label">Lever:</InputLabel>
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              id="new-price-modal-lever"
              label="Select New Lever"
              value={lever?.id || ''}
              data-testid="new-price-modal-lever"
              disabled={!category || inUpdateMode}
              {...baseTextFieldProps}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setLever(props.pricingCatalog[event.target.value as any]);
              }}
            >
              <MenuItem key={''} value={''}>
                {''}
              </MenuItem>
              {leverOptions?.map((leverOption) => (
                <MenuItem key={leverOption.id} value={leverOption.id}>
                  {leverOption.display_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <InputLabel className="new-price-modal-label">UOM:</InputLabel>
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              id="new-price-modal-uom"
              label="Select UOM"
              value={unitOfMeasure}
              data-testid="new-price-modal-uom"
              disabled={!lever}
              {...baseTextFieldProps}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setUnitOfMeasure(event.target.value as any);
              }}
            >
              <MenuItem key={''} value={''}>
                {''}
              </MenuItem>
              {uomOptions?.map((uomOption) => (
                <MenuItem key={uomOption} value={uomOption}>
                  {uomOption}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <InputLabel className="new-price-modal-label">SH Price:</InputLabel>
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="new-price-modal-shipper-price"
              type="number"
              label="#.##"
              value={shipperPrice}
              data-testid="new-price-modal-shipper-price"
              {...baseTextFieldProps}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setShipperPrice(event.target.value as any);
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <InputLabel className="new-price-modal-label">WH Price:</InputLabel>
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="new-price-modal-warehouse-price"
              type="number"
              label="#.##"
              value={warehousePrice}
              data-testid="new-price-modal-warehouse-price"
              {...baseTextFieldProps}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setWarehousePrice(event.target.value as any);
              }}
            />
          </Grid>
        </Grid>
        <h3>Modifiers</h3>
        {renderSelectedFilterOptions()}
        <div>
          <Button
            className="new-price-modal-new-filter-button"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addNewFilterOption}
            disabled={!lever}
          >
            Add Modifier
          </Button>
        </div>
        {fullRateDescription && (
          <div>
            <Grid container columnSpacing={0} rowSpacing={2}>
              <Grid item xs={12}>
                <h3>Displays on bill as: </h3>
              </Grid>
              <Grid item xs={12}>
                <div data-testid="full-rate-description">{fullRateDescription}</div>
              </Grid>
              <Grid item xs={3}>
                <Button onClick={() => setEditSuffix(!editSuffix)}>
                  {editSuffix ? 'Cancel' : 'Edit Line Description'}
                </Button>
              </Grid>
              <Grid item xs={9}>
                {editSuffix && (
                  <TextField
                    data-testid="new-price-modal-lever-suffix"
                    value={leverSuffix}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setLeverSuffix(event.target.value as any);
                    }}
                  ></TextField>
                )}
              </Grid>
            </Grid>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={submitButtonClicked}>
          Confirm Pricing Lever
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNewPriceModal;
